<template>
  <div v-show="showGrid" class="content-grid critical-component-clear">
    <div ref="contentGrid" class="container">
      <div
        v-if="liquid.sectionTitle || liquid.sectionSubtitle"
        class="content-grid__header"
      >
        <div class="content-grid__header-text">
          <h2
            v-if="liquid.sectionTitle"
            class="content-grid__title text-mobile-headlines-h3 text-desktop-headline-h3"
            v-text="liquid.sectionTitle"
          />
          <p
            v-if="liquid.sectionSubtitle"
            class="text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"
            v-text="liquid.sectionSubtitle"
          />
        </div>
      </div>
      <content-drawer
        v-if="liquid.showFilter"
        :filter-options="filterOptions"
        :filters="filters"
        :total-items-label="totalItemsLabel"
        @toggle-filter="(payload) => toggleFilter(payload.key, payload.option)"
      />
      <content-filter
        :show-search="liquid.showSearch"
        :show-filter="liquid.showFilter"
        :filters="filters"
        :search-query="searchQuery"
        :items-count="itemsCount"
        :total-items-label="totalItemsLabel"
        :no-result-message="$string('plans.no_results_html')"
        @update-search-query="(value) => (searchQuery = value)"
        @reset-filters="resetFilters"
        @toggle-filter="(payload) => toggleFilter(payload.key, payload.option)"
      />

      <div class="content-grid__list grid">
        <div class="col xs-span l12 content-grid__filters">
          <div></div>
          <p
            class="content-grid__item-count text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
            :class="{
              [$class('loading')]: ['loading', 'initial-load'].includes(state),
            }"
            v-text="totalItemsLabel"
          />
        </div>
        <div
          v-for="(item, index) of paginatedItems"
          :key="index"
          :class="itemClass"
        >
          <content-card
            :badge="item.badge"
            :title="item.title"
            :description="item.description"
            :level="item.level"
            :goals="item.goals"
            :cta-label="liquid.ctaLabel"
            :cta-url="item.onlineStoreUrl"
            :teaser-image="item.teaser_image"
            :display-features="item.displayFeatures"
          />
        </div>
      </div>

      <nav v-if="totalPages > 1" class="pagination" role="navigation">
        <span
          class="visually-hidden"
          v-text="$string('general.pagination.label')"
        />

        <ul class="pagination__list">
          <li v-if="currentPage > 1" class="pagination__list-item">
            <a
              class="pagination__button pagination__part"
              :href="getPageUrl(currentPage - 1)"
              @click.prevent="goToPage(currentPage - 1)"
            >
              <span
                class="visually-hidden"
                v-text="$string('general.pagination.previous')"
              />
              <icon-arrow-left />
            </a>
          </li>

          <li
            v-for="page of totalPages"
            :key="page"
            class="pagination__list-item"
          >
            <span
              v-if="page === currentPage"
              :class="getPageClass(page)"
              aria-current="page"
              data-active="true"
            >
              <span
                class="visually-hidden"
                v-text="$string('general.pagination.page', { page })"
              />
              {{ page }}
            </span>

            <a
              v-else
              class="pagination__link pagination__part text-body-m-regular"
              :href="getPageUrl(page)"
              @click.prevent="goToPage(page)"
            >
              <span
                class="visually-hidden"
                v-text="$string('general.pagination.page', { page })"
              />
              {{ page }}
            </a>
          </li>

          <li v-if="currentPage < totalPages" class="pagination__list-item">
            <a
              class="pagination__button pagination__part"
              :href="getPageUrl(currentPage + 1)"
              @click.prevent="goToPage(currentPage + 1)"
            >
              <span
                class="visually-hidden"
                v-text="$string('general.pagination.next')"
              />
              <icon-arrow-right />
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
/**
 * Vue: content-grid (content-grid)
 * -----------------------------------------------------------------------------
 * Content Grid for plans.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.sectionTitle - Section setting title.
 * @param {String} liquid.showFilter - Filter setting (show filter)
 * @param {String} liquid.showSearch - Filter setting (show search)
 * @param {String} liquid.ctaLabel - CTA label.
 * @param {String} liquid.placeholderCount - Placeholder count.
 * @param {String} liquid.filterNameSingular - Filter name singular.
 * @param {String} liquid.filterNamePlural - Filter name plural.
 * @param {String} liquid.source - Product source.
 * @param {String} liquid.metaobject - Metaobject handle referencing products.
 * @param {Number} liquid.limit - Product limit.
 * @param {Number} liquid.limitBatch - Batch limit for requests.
 *
 */

import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg';
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg';
import ContentCard from '~async/content-card/content-card';
import ContentDrawer from '~async/content-drawer/content-drawer';
import ContentFilter from '~async/content-filter/content-filter';
import metaobjectsQuery from '~graphql/metaobjects.gql';
import { scrollToElement } from '~/helpers/general';
import { transformEdgesToObject } from '~/format/format-content-metaobject';

export default {
  name: 'ContentGrid',

  components: {
    IconArrowLeft,
    IconArrowRight,
    ContentCard,
    ContentDrawer,
    ContentFilter,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        sectionSubtitle: '',
        sectionTitle: '',
        showFilter: true,
        showSearch: true,
        ctaLabel: '',
        filterNameSingular: '',
        filterNamePlural: '',
        source: '',
        limit: 4,
        limitBatch: 50,
        metaobject: 'trainingplans',
        placeholderCount: 8,
      }),
    },
  },

  data() {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      items: Array.from({ length: this.liquid.placeholderCount }, () => false),
      itemsCount: 0,
      state: 'loading',
      searchQuery: urlParams.get('q') || '',
      filters: {},
      removeFilters: {},
      filterOptions: {},
      endCursor: null,
      hasNextPage: false,
      currentPage: parseInt(urlParams.get('page')) || 1,
      itemsPerPage: this.liquid.limit,
    };
  },

  computed: {
    /**
     * Compute product item class.
     * @returns {String}
     */
    itemClass() {
      return `content-grid__content col xs-span m4`;
    },

    /**
     * Compute query variables.
     * @returns {Object}
     */
    queryVariables() {
      const { limitBatch, metaobject } = this.liquid;
      return {
        country: this.$variable('store.country'),
        type: metaobject,
        language: this.$variable('store.language'),
        first: limitBatch,
        // sortKey: 'id' or 'updated_at'
        sortKey: 'updated_at',
        reverse: false,
      };
    },

    /**
     * Compute whether to show carousel.
     * - Hide 'last-seen' carousel by default.
     * @returns {Boolean}
     */
    showGrid() {
      if (this.state === 'loading') {
        return false;
      }

      return this.items.length > 0;
    },

    /**
     * Search and filter items.
     * - Filtered items
     * @returns {Object}
     */
    filteredItems() {
      if (this.state !== 'ready' || !this.items) {
        return [];
      }

      return this.items?.filter((item) => {
        // Filter based on search query

        const lowerCaseQuery = this.searchQuery.toLowerCase();

        if (
          !item.title.toLowerCase().includes(lowerCaseQuery) &&
          !item.description.toLowerCase().includes(lowerCaseQuery)
        ) {
          return false;
        }

        let filterApplies = false;
        let noFilterSelected = true;

        // Filter based on selected filters
        for (const [key, values] of Object.entries(this.filters)) {
          noFilterSelected = values.length === 0 && noFilterSelected;
          filterApplies =
            item.filterFeatures.filter((feature) => {
              return feature.key === key && values.includes(feature.value);
            }).length > 0 || filterApplies;
        }

        return filterApplies || noFilterSelected;
      });
    },

    /**
     * Paginated items.
     * @returns {Array}
     */
    paginatedItems() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.state === 'ready' && this.filteredItems.slice(start, end);
    },

    /**
     * Compute total pages.
     * set to 1 if no items are found
     * @returns {Number}
     */
    totalPages() {
      return Math.max(
        1,
        Math.ceil(this.filteredItems.length / this.itemsPerPage),
      );
    },

    /**
     * Compute label text with currently shown plans.
     * @returns {String}
     */
    totalItemsLabel() {
      if (this.itemsCount === 1) {
        return `(${this.$string('plans.count_single', {
          replace: {
            count: this.itemsCount,
          },
        })})`;
      }

      return `(${this.$string('plans.count_plural', {
        replace: {
          count: this.itemsCount,
        },
      })})`;
    },
  },

  watch: {
    /**
     * Watch search query.
     */
    searchQuery() {
      this.resetPagination();
      this.updateURL();
    },

    /**
     * Watch current page.
     */
    currentPage() {
      this.updateURL();
    },

    /**
     * Watch items.
     * Ensure currentPage stays within bounds
     */
    filteredItems() {
      this.itemsCount = this.filteredItems?.length;
      const totalPages = Math.max(
        1,
        Math.ceil(this.itemsCount / this.itemsPerPage),
      );
      this.currentPage = Math.min(this.currentPage, totalPages);
    },
  },

  mounted() {
    this.fetchMetaobjects();
  },

  methods: {
    /**
     * Reset pagination.
     */
    resetPagination() {
      this.currentPage = 1;
    },

    /**
     * Transform plan features.
     * @param {Array} items - Plan items.
     * @returns {Array}
     */
    transformPlanFeatures(items) {
      return items.map((item) => {
        const displayFeatures = item.feature_items
          ?.filter((featureItem) => featureItem.use_in_card_and_header)
          .map((featureItem) => {
            return {
              description: featureItem.description,
              icon: featureItem.icon?.image.url ?? '',
            };
          });

        const filterFeatures = item.feature_items
          ?.filter((featureItem) => featureItem.use_in_filter)
          .map((featureItem) => {
            return {
              key: featureItem.criteria,
              value: featureItem.description,
            };
          });
        return {
          ...item,
          displayFeatures: displayFeatures ?? [],
          filterFeatures: filterFeatures ?? [],
        };
      });
    },

    /**
     * Fetch metaobjects based on type.
     */
    async fetchMetaobjects(cursor = null) {
      const response = await cnvs.Query({
        query: metaobjectsQuery,
        variables: {
          ...this.queryVariables,
          after: cursor,
        },
      });

      const newItems = this.transformPlanFeatures(
        transformEdgesToObject(response?.metaobjects),
      );
      if (cursor === null) {
        this.items = newItems;
      } else {
        this.items = [...this.items, ...newItems];
      }
      this.endCursor = response?.metaobjects.pageInfo.endCursor;
      this.hasNextPage = response?.metaobjects.pageInfo.hasNextPage;

      if (this.hasNextPage) {
        await this.fetchMetaobjects(this.endCursor);
      } else {
        if (this.liquid.showFilter) {
          this.generateFilterOptions();
          this.parseURLParams();
        }
        this.state = 'ready';
      }
    },

    /**
     * Toggle filter.
     * @param {String} key - Filter key.
     * @param {String} value - Filter value.
     */
    toggleFilter(key, value) {
      if (!this.filters[key]) {
        this.filters[key] = [];
      }

      const index = this.filters[key].indexOf(value);
      if (index === -1) {
        this.filters[key].push(value);
      } else {
        this.filters[key].splice(index, 1);
        this.removeFilters[key] = [value];
      }

      this.updateURL();
    },

    /**
     * Reset all filters.
     */
    resetFilters() {
      this.searchQuery = '';
      this.removeFilters = { ...this.filters };
      this.filters = {};
      this.currentPage = 1;
      this.updateURL();
    },

    /**
     * Generate filter options.
     */
    generateFilterOptions() {
      this.filterOptions = {};

      for (const item of this.items) {
        item.filterFeatures.forEach((filterFeature) => {
          const key = filterFeature.key;
          const value = filterFeature.value;

          if (!this.filterOptions[key]) {
            this.filterOptions[key] = new Set();
          }

          this.filterOptions[key].add(value);
        });
      }

      for (const [key, values] of Object.entries(this.filterOptions)) {
        this.filterOptions[key] = Array.from(values);
      }
    },

    /**
     * Parse URL parameters for the filters.
     */
    parseURLParams() {
      const urlParams = new URLSearchParams(window.location.search);

      this.searchQuery = urlParams.get('q') || '';

      for (const [key, value] of urlParams.entries()) {
        if (key !== 'q' && key !== 'page') {
          // accept only valid filter keys
          if (key in this.filterOptions) {
            this.filters[key] = value.split(',');
          }
        }
      }

      this.currentPage = parseInt(urlParams.get('page')) || 1;
    },

    /**
     * Navigate to a specific page.
     * Ensures page number is within valid bounds.
     * @param {Number} page - Page number to go to.
     */
    goToPage(page) {
      this.currentPage = Math.max(1, Math.min(page, this.totalPages));
      this.$nextTick().then(() => {
        const carouselElement = this.$refs.contentGrid;
        if (carouselElement) {
          scrollToElement(carouselElement);
        }
      });
    },

    /**
     * Get pagination URL.
     * Ensures page number is within valid bounds.
     * Resets own URL parameters while retaining other ones.
     * @param {Number} page - Page number to go to.
     */
    getPageUrl(page) {
      const url = new URL(window.location.href);
      // clear only our own URL parameters
      url.searchParams.delete('q');
      url.searchParams.delete('page');
      Object.keys(this.removeFilters).forEach((key) => {
        url.searchParams.delete(key);
      });

      if (this.searchQuery) {
        url.searchParams.set('q', this.searchQuery);
      }
      if (page > 1) {
        url.searchParams.set('page', page);
      }

      // Add filters back with the right values if there are any
      Object.keys(this.filters).forEach((key) => {
        if (this.filters[key].length) {
          url.searchParams.set(key, this.filters[key].join(','));
        }
      });

      return url;
    },

    /**
     * Get page class.
     * @param {Number} page - Page number.
     * @returns {String}
     */
    getPageClass(page) {
      return page === this.currentPage
        ? 'pagination__part text-body-m-regular is-active'
        : 'pagination__part text-body-m-regular';
    },

    /**
     * Update URL.
     */
    updateURL() {
      window.history.pushState({}, '', this.getPageUrl(this.currentPage));
      this.removeFilters = {};
    },
  },
};
</script>

<style lang="scss">
@import '@/components/pagination';
@import './content-grid';
</style>
